import React, { useState, useEffect } from 'react';
import { useChangePassword } from '@/hooks/useChangePassword';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { loading, error, changePassword, clearError } = useChangePassword();

  // Estado de validación visual
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
  });

  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // Actualizar validación en tiempo real
  useEffect(() => {
    setPasswordValidation({
      minLength: newPassword.length >= 8,
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};:,.<>?]/.test(newPassword),
    });
  }, [newPassword]);

  useEffect(() => {
    setPasswordsMatch(newPassword === confirmPassword || confirmPassword === '');
  }, [newPassword, confirmPassword]);

  const isValidPassword = Object.values(passwordValidation).every(v => v);
  const isFormValid =
    currentPassword &&
    newPassword &&
    confirmPassword &&
    isValidPassword &&
    passwordsMatch &&
    !loading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const success = await changePassword({
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });

    if (success) {
      // Limpiar formulario y cerrar
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      clearError();
      onSuccess?.();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-lg">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-camping-800">Cambiar Contraseña</h2>
          <p className="text-sm text-camping-600 mt-1">
            Actualiza tu contraseña de forma segura
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Password */}
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-camping-800 mb-2">
              Contraseña Actual
            </label>
            <input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                clearError();
              }}
              className="w-full border border-camping-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-camping-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Ingresa tu contraseña actual"
              required
              disabled={loading}
            />
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-camping-800 mb-2">
              Nueva Contraseña
            </label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                clearError();
              }}
              className="w-full border border-camping-200 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-camping-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Ingresa tu nueva contraseña"
              required
              disabled={loading}
            />

            {/* Password Requirements */}
            {newPassword && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-camping-700">Requisitos:</p>
                <ul className="space-y-1 text-xs">
                  <li className={`flex items-center gap-2 ${passwordValidation.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.minLength ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {passwordValidation.minLength && '✓'}
                    </span>
                    Mínimo 8 caracteres
                  </li>
                  <li className={`flex items-center gap-2 ${passwordValidation.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasUppercase ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {passwordValidation.hasUppercase && '✓'}
                    </span>
                    Al menos una MAYÚSCULA
                  </li>
                  <li className={`flex items-center gap-2 ${passwordValidation.hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasLowercase ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {passwordValidation.hasLowercase && '✓'}
                    </span>
                    Al menos una minúscula
                  </li>
                  <li className={`flex items-center gap-2 ${passwordValidation.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasNumber ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {passwordValidation.hasNumber && '✓'}
                    </span>
                    Al menos un NÚMERO
                  </li>
                  <li className={`flex items-center gap-2 ${passwordValidation.hasSpecial ? 'text-green-600' : 'text-gray-500'}`}>
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasSpecial ? 'bg-green-100' : 'bg-gray-100'}`}>
                      {passwordValidation.hasSpecial && '✓'}
                    </span>
                    Al menos un carácter especial (!@#$%...)
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-camping-800 mb-2">
              Confirmar Contraseña
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                clearError();
              }}
              className={`w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${
                !passwordsMatch && confirmPassword
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-camping-200 focus:ring-camping-500'
              }`}
              placeholder="Confirma tu nueva contraseña"
              required
              disabled={loading}
            />
            {!passwordsMatch && confirmPassword && (
              <p className="text-xs text-red-600 mt-1">Las contraseñas no coinciden</p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-camping-300 text-camping-700 rounded-lg hover:bg-camping-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!isFormValid}
              className="flex-1 px-4 py-2 bg-camping-600 text-white rounded-lg hover:bg-camping-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.25" />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Guardando...
                </>
              ) : (
                'Cambiar Contraseña'
              )}
            </button>
          </div>
        </form>

        {/* Info Text */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Tu contraseña se sincronizará con tu cuenta de seguridad
        </p>
      </div>
    </div>
  );
};
